import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../store/authStore';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { userService, UserPreferences } from '../../services/userService';
import { corpusService } from '../../services/corpusService';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faKey, faRobot } from '@fortawesome/free-solid-svg-icons';

export default function SettingsPage() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  // State for preferences form
  const [preferences, setPreferences] = useState<Partial<UserPreferences>>({});
  const [showOpenAiKey, setShowOpenAiKey] = useState(false);
  const [showClaudeKey, setShowClaudeKey] = useState(false);

  // Fetch user preferences
  const { data: preferencesData, isLoading: loadingPreferences } = useQuery({
    queryKey: ['user-preferences'],
    queryFn: () => userService.getPreferences(),
  });

  // Fetch sectors for dropdown
  const { data: sectorsData } = useQuery({
    queryKey: ['sectors'],
    queryFn: () => corpusService.getSectors(),
  });

  // Initialize form when data loads
  useEffect(() => {
    if (preferencesData?.preferences) {
      setPreferences(preferencesData.preferences);
    }
  }, [preferencesData]);

  // Mutation to update preferences
  const updatePreferencesMutation = useMutation({
    mutationFn: userService.updatePreferences,
    onSuccess: () => {
      toast.success('Preferencias guardadas correctamente');
      queryClient.invalidateQueries({ queryKey: ['user-preferences'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al guardar preferencias');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updatePreferencesMutation.mutate(preferences);
  };

  const handleChange = (field: keyof UserPreferences, value: any) => {
    setPreferences((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  if (loadingPreferences) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Cargando configuración...</div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-blue-600 rounded-xl p-8 shadow-lg">
        <h1 className="text-3xl font-bold mb-2 text-white">Configuración</h1>
        <p className="text-white/90">Gestiona tu cuenta y preferencias</p>
      </div>

      {/* Profile */}
      <Card>
        <CardHeader>
          <CardTitle>Perfil</CardTitle>
          <CardDescription>Información de tu cuenta de usuario</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <input
                type="email"
                className="w-full px-3 py-2 border border-border rounded-md bg-muted text-muted-foreground cursor-not-allowed"
                value={user?.email}
                disabled
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Nombre completo</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent bg-background"
                placeholder="Tu nombre"
                defaultValue={user?.fullName}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Organización</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent bg-background"
                placeholder="Tu organización"
                defaultValue={user?.organization}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Analysis Preferences */}
      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Preferencias de Análisis</CardTitle>
            <CardDescription>Configura tus preferencias de análisis de texto</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Sector predeterminado</label>
                <select
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent bg-background"
                  value={preferences.defaultSectorId || ''}
                  onChange={(e) =>
                    handleChange('defaultSectorId', e.target.value ? parseInt(e.target.value) : null)
                  }
                >
                  <option value="">Ninguno</option>
                  {sectorsData?.sectors.map((sector) => (
                    <option key={sector.id} value={sector.id}>
                      {sector.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Nivel objetivo MCERL</label>
                <select
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent bg-background"
                  value={preferences.targetMcerlLevel || 'B2'}
                  onChange={(e) => handleChange('targetMcerlLevel', e.target.value)}
                >
                  <option value="A1">A1 - Principiante</option>
                  <option value="A2">A2 - Elemental</option>
                  <option value="B1">B1 - Intermedio</option>
                  <option value="B2">B2 - Intermedio Alto</option>
                  <option value="C1">C1 - Avanzado</option>
                  <option value="C2">C2 - Maestría</option>
                </select>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="ai"
                  className="w-4 h-4 rounded border-border"
                  checked={preferences.enableAiSimplification ?? true}
                  onChange={(e) => handleChange('enableAiSimplification', e.target.checked)}
                />
                <label htmlFor="ai" className="text-sm">
                  Habilitar simplificación con IA
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* AI Configuration */}
        {preferences.enableAiSimplification && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FontAwesomeIcon icon={faRobot} className="text-blue-600" />
                Configuración de IA
              </CardTitle>
              <CardDescription>
                Configura las claves API y modelos de IA para la simplificación de textos
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* AI Provider Selection */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Proveedor de IA
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <label
                      className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                        preferences.aiProvider === 'CHATGPT'
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-border hover:border-blue-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="aiProvider"
                        value="CHATGPT"
                        checked={preferences.aiProvider === 'CHATGPT'}
                        onChange={(e) => handleChange('aiProvider', e.target.value)}
                        className="mr-3"
                      />
                      <div>
                        <div className="font-semibold">ChatGPT</div>
                        <div className="text-xs text-muted-foreground">OpenAI</div>
                      </div>
                    </label>
                    <label
                      className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                        preferences.aiProvider === 'CLAUDE'
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-border hover:border-blue-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="aiProvider"
                        value="CLAUDE"
                        checked={preferences.aiProvider === 'CLAUDE'}
                        onChange={(e) => handleChange('aiProvider', e.target.value)}
                        className="mr-3"
                      />
                      <div>
                        <div className="font-semibold">Claude</div>
                        <div className="text-xs text-muted-foreground">Anthropic</div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* OpenAI Configuration */}
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold flex items-center gap-2">
                    <FontAwesomeIcon icon={faKey} className="text-green-600" />
                    OpenAI (ChatGPT)
                  </h3>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      API Key
                      {preferences.aiProvider === 'CHATGPT' && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                    </label>
                    <div className="relative">
                      <input
                        type={showOpenAiKey ? 'text' : 'password'}
                        className="w-full px-3 py-2 pr-10 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent bg-background"
                        placeholder="sk-..."
                        value={preferences.openaiApiKey || ''}
                        onChange={(e) => handleChange('openaiApiKey', e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => setShowOpenAiKey(!showOpenAiKey)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <FontAwesomeIcon icon={showOpenAiKey ? faEyeSlash : faEye} />
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Obtén tu clave en{' '}
                      <a
                        href="https://platform.openai.com/api-keys"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        platform.openai.com
                      </a>
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Modelo</label>
                    <select
                      className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent bg-background"
                      value={preferences.openaiModel || 'gpt-5'}
                      onChange={(e) => handleChange('openaiModel', e.target.value)}
                    >
                      <option value="gpt-5">GPT-5 (Recomendado - Agosto 2025)</option>
                      <option value="gpt-5-mini">GPT-5 Mini (Rápido y económico)</option>
                      <option value="gpt-5-nano">GPT-5 Nano (Muy eficiente)</option>
                      <option value="gpt-4o">GPT-4o (Multimodal)</option>
                      <option value="gpt-4o-mini">GPT-4o Mini</option>
                    </select>
                    <p className="text-xs text-muted-foreground mt-1">
                      GPT-5 incluye razonamiento integrado y mejor comprensión contextual
                    </p>
                  </div>
                </div>

                {/* Claude Configuration */}
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold flex items-center gap-2">
                    <FontAwesomeIcon icon={faKey} className="text-orange-600" />
                    Anthropic (Claude)
                  </h3>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      API Key
                      {preferences.aiProvider === 'CLAUDE' && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                    </label>
                    <div className="relative">
                      <input
                        type={showClaudeKey ? 'text' : 'password'}
                        className="w-full px-3 py-2 pr-10 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent bg-background"
                        placeholder="sk-ant-..."
                        value={preferences.claudeApiKey || ''}
                        onChange={(e) => handleChange('claudeApiKey', e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => setShowClaudeKey(!showClaudeKey)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <FontAwesomeIcon icon={showClaudeKey ? faEyeSlash : faEye} />
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Obtén tu clave en{' '}
                      <a
                        href="https://console.anthropic.com/settings/keys"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        console.anthropic.com
                      </a>
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Modelo</label>
                    <select
                      className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent bg-background"
                      value={preferences.claudeModel || 'claude-sonnet-4-5-20250929'}
                      onChange={(e) => handleChange('claudeModel', e.target.value)}
                    >
                      <option value="claude-sonnet-4-5-20250929">Claude Sonnet 4.5 (Recomendado - Septiembre 2025)</option>
                      <option value="claude-opus-4-1">Claude Opus 4.1 (Más capaz - Agosto 2025)</option>
                      <option value="claude-haiku-4-5">Claude Haiku 4.5 (Rápido y económico - Octubre 2025)</option>
                      <option value="claude-sonnet-4">Claude Sonnet 4 (Mayo 2025)</option>
                      <option value="claude-opus-4">Claude Opus 4 (Mayo 2025)</option>
                    </select>
                    <p className="text-xs text-muted-foreground mt-1">
                      Claude Sonnet 4.5 ofrece el mejor equilibrio entre calidad y velocidad
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="mt-6 flex justify-end">
          <Button type="submit" disabled={updatePreferencesMutation.isPending}>
            {updatePreferencesMutation.isPending ? 'Guardando...' : 'Guardar preferencias'}
          </Button>
        </div>
      </form>
    </div>
  );
}
